<template>
  <section class="typification">
    <draggable :list="typification" item-key="order" @change="changeDrag($event)" :move="checkMove">
      <template #item="{ element, index }">
        <TypificationItem
          :key="index"
          :index="index"
          :item="element"
          :name="`Grupo ${index + 1}`"
          :total="totalGroups"
          type="group"
          @updateTree="updateTree"
          @onToggleStatus="handleToggleStatus($event, element, `Grupo ${index + 1}`, element.id)"
        >
          <template #edit="{ hideMoreOptions }">
            <ResultAdd
              :item="element"
              @push="handlePush($event, element.children)"
              @click="hideMoreOptions"
            />
            <GroupEdit
              class="item__edit"
              :item="element"
              @change="handleChange($event, element)"
              @click="hideMoreOptions"
            />

            <a-button
              type="text"
              @click="
                handleDeleteItem($event, element, `Grupo ${index + 1}`, element.id);
                hideMoreOptions();
              "
            >
              <template #icon>
                <DeleteOutlined />
              </template>

              Eliminar grupo
            </a-button>
          </template>
          <TypificationResult
            :group="element"
            @onChange="handleChange"
            @onPush="handlePush"
            @onDrag="changeDrag"
            @onDelete="handleDeleteItem"
            @onToggleStatus="handleToggleStatus"
            @updateTree="updateTree"
          />
        </TypificationItem>
      </template>
    </draggable>
  </section>
</template>

<script>
/* eslint-disable @typescript-eslint/no-unused-vars */
import TypificationItem from "@/app/entities/components/picker/TypificationItem.vue";
import GroupEdit from "@/app/entities/components/drawers/typifications/TypificationGroupEdit.vue";
import ResultAdd from "@/app/entities/components/drawers/typifications/TypificationResult.vue";
import TypificationResult from "@/app/entities/components/picker/TypificationResult.vue";
import { defineComponent, createVNode } from "vue";
import { DeleteOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import { Modal, message } from "ant-design-vue";
import draggable from "vuedraggable";
import { useStore } from "vuex";

export default defineComponent({
  components: {
    DeleteOutlined,
    TypificationItem,
    GroupEdit,
    ResultAdd,
    draggable,
    TypificationResult,
  },
  props: {
    customizedManagement: {
      required: true,
      default: {},
    },
    equivalencies: {
      required: true,
      type: Array,
    },
  },
  setup(props, { emit }) {
    const store = useStore();

    const isInCustomizedManagement = (groupId, resultId, motiveId, submotiveId) => {
      if (submotiveId) {
        return props.customizedManagement?.sub_motive_type === submotiveId;
      }

      if (motiveId) {
        return props.customizedManagement?.motive_type === motiveId;
      }

      if (resultId) {
        return props.customizedManagement?.result_type === resultId;
      }

      if (groupId) {
        return props.customizedManagement?.group_type === groupId;
      }

      return false;
    };

    const deleteEquivalencyText = {
      group:
        "Recuerda que algún resultado de este grupo está configurado como equivalencia del tipo de grupo “No contacto” y deberás realizar nuevamente la configuración para lanzar estrategias de tipo predictivo. Además, todos los motivos y submotivos que se encuentren en este resultado también serán eliminados.",
      result:
        "Recuerda que este resultado está configurado como equivalencia del tipo de grupo “No contacto” y deberás realizar nuevamente la configuración para lanzar estrategias de tipo predictivo. Además, todos los motivos y submotivos que se encuentren en este resultado también serán eliminados.",
    };

    const handleDeleteItem = (_, item, itemIndex, groupId, resultId, motiveId, submotiveId) => {
      if (isInCustomizedManagement(groupId, resultId, motiveId, submotiveId)) {
        Modal.warning({
          title: warnTitle[item.type],
          content: warnText[item.type],
        });
      } else {
        Modal.confirm({
          title: `¿Estás seguro de eliminar el "${itemIndex} - ${item.name}"?`,
          content: props.equivalencies.some(({ result }) => result === resultId)
            ? deleteEquivalencyText[item.type]
            : getTextDelete(item.type),
          icon: createVNode(QuestionCircleOutlined),
          onOk: async () => {
            await store.dispatch("deleteTypificationItemByType", {
              type: item.type,
              id: item.id,
              groupId,
              resultId,
              motiveId,
              submotiveId,
            });
            message.success(`Se ha eliminado el "${itemIndex} - ${item.name}"`);
            emit("updateTree");
          },
          centered: true,
          okText: "Eliminar",
          cancelText: "Cancelar",
        });
      }
    };

    const handleToggleStatus = (_, item, itemIndex, groupId, resultId, motiveId, submotiveId) => {
      const copyName = item.name;
      if (item.status == 1) {
        Modal.confirm({
          title: `¿Estás seguro de desactivar el "${itemIndex} - ${copyName}"?`,
          content: getTextContent(item.type, item.status),
          icon: createVNode(QuestionCircleOutlined),
          onOk: async () => {
            try {
              await store.dispatch("updateItemToTypificationTree", {
                id: item.id,
                type: item.type,
                group_type: item.group_type,
                status: 2,
              });
              message.success(`Se ha desactivado el "${itemIndex} - ${copyName}"?`);
              emit("updateTree");
            } catch (error) {
              const errorMessage = error?.getErrorMessage() || `Ha ocurrido un error`;

              this.$message.error(errorMessage);
            }
          },
          centered: true,
          okText: "Desactivar",
          cancelText: "Cancelar",
        });
      } else {
        Modal.confirm({
          title: `¿Estás seguro de activar el "${itemIndex} - ${copyName}"?`,
          content: getTextContent(item.type, item.status),
          icon: createVNode(QuestionCircleOutlined),
          onOk: async () => {
            try {
              await store.dispatch("updateItemToTypificationTree", {
                id: item.id,
                type: item.type,
                group: item.group_type,
                status: 1,
              });
              message.success(`Se ha activado el "${itemIndex} - ${copyName}"?`);
              emit("updateTree");
            } catch (error) {
              const errorMessage = error?.getErrorMessage() || `Ha ocurrido un error`;

              this.$message.error(errorMessage);
            }
          },
          centered: true,
          okText: "Activar",
          cancelText: "Cancelar",
        });
      }
    };

    const getTextContent = (type, status) => {
      if (status == 1) {
        switch (type) {
          case "group":
            return "Recuerda que todos los resultados, motivos y submotivos que se encuentren en este grupo también serán desactivados.";
          case "result":
            return "Recuerda que todos los motivos y submotivos que se encuentren en este resultado también serán desactivados.";
          case "motive":
            return "Recuerda que todos los submotivos que se encuentren en este motivo también serán desactivados.  ";
          case "submotive":
            return "Podrás volverlo a activar cuando desees.";
          default:
            return "Recuerda que todo será desactivado";
        }
      } else {
        switch (type) {
          case "group":
            return "Recuerda que debes activar los resultados, motivos y submotivos que desees que vuelvan a estar activos. ";
          case "result":
            return "Recuerda que el grupo que lo contienen también será activado.";
          case "motive":
            return "Recuerda que el grupo y resultado que lo contienen también serán activados.";
          case "submotive":
            return "Recuerda que el grupo, resultado y motivo que lo contienen también serán activados.";
          default:
            return "Recuerda que todo será activado";
        }
      }
    };

    const getTextDelete = (type) => {
      switch (type) {
        case "group":
          return "Recuerda que todos los resultados, motivos y submotivos que se encuentren en este grupo también serán eliminados.";
        case "result":
          return "Recuerda que todos los motivos y submotivos que se encuentren en este resultado también serán eliminados.";
        case "motive":
          return "Recuerda que todos los submotivos que se encuentren en este motivo también serán eliminados.";
        case "submotive":
          return "No vovlerás a verlo en tus tipificaciones.  ";
        default:
          return "";
      }
    };

    const warnTitle = {
      group: "Este grupo se encuentra asociado a una tipificación por defecto",
      result: "Este resultado se encuentra asociado a una tipificación por defecto",
      motive: "Este motivo se encuentra asociado a una tipificación por defecto",
      submotive: "Este submotivo se encuentra asociado a una tipificación por defecto",
    };

    const warnText = {
      group:
        "Recuerda que para eliminar este grupo, primero debes editar tu tipificación por defecto actual.",
      result:
        "Recuerda que para eliminar este resultado, primero debes editar tu tipificación por defecto actual.",
      motive:
        "Recuerda que para eliminar este motivo, primero debes editar tu tipificación por defecto actual.",
      submotive:
        "Recuerda que para eliminar este submotivo, primero debes editar tu tipificación por defecto actual.",
    };

    return {
      handleDeleteItem,
      handleToggleStatus,
    };
  },
  data() {
    return {
      copyTree: [],
    };
  },
  mounted() {
    this.copyTree = this.typification;
  },
  computed: {
    typificationTypeDefault() {
      return this.$store.getters["getTypificationDefault"];
    },
    typification: {
      get() {
        return this.$store.getters["getTypificationTree"];
      },
      set(value) {
        this.$store.dispatch("updateTypificationTree", value);
      },
    },
    totalGroups() {
      return this.typification.length;
    },
  },
  methods: {
    checkMove($event) {
      const futureIndex = $event.draggedContext.futureIndex;
      const currentIndex = $event.draggedContext.index;
      if (this.typification[futureIndex].group_type == this.typification[currentIndex].group_type) {
        return true;
      }
      return false;
    },
    async changeDrag($event, ...args) {
      const groupId = args[0];
      const item = $event.moved.element;
      const newIndex = $event.moved.newIndex;
      const oldIndex = $event.moved.oldIndex;
      let message = "";
      const payload = {};
      switch (item.type) {
        case "group": {
          payload.pallete = "groups";
          payload.elements = [];
          let element;
          if (newIndex > oldIndex) {
            for (let i = oldIndex; i <= newIndex; i++) {
              if (i == newIndex) {
                element = {
                  id: this.typification[oldIndex].id,
                  order: this.typification[newIndex].order,
                };
              } else {
                element = {
                  id: this.typification[i + 1].id,
                  order: this.typification[i].order,
                };
              }
              payload.elements.push(element);
            }
            message = `El ${item.name} ha bajado ${newIndex - oldIndex} posiciones`;
          } else {
            for (let i = oldIndex; i >= newIndex; i--) {
              if (i == newIndex) {
                element = {
                  id: this.typification[oldIndex].id,
                  order: this.typification[newIndex].order,
                };
              } else {
                element = {
                  id: this.typification[i - 1].id,
                  order: this.typification[i].order,
                };
              }
              payload.elements.push(element);
            }
            message = `El ${item.name} ha subido ${oldIndex - newIndex} posiciones`;
          }
          break;
        }
        case "result": {
          payload.pallete = "results";
          payload.elements = [];
          const results = this.typification.find((t) => t.id == groupId).results;
          let element;
          if (newIndex > oldIndex) {
            for (let i = oldIndex; i <= newIndex; i++) {
              if (i == newIndex) {
                element = {
                  id: results[oldIndex].id,
                  order: results[newIndex].order,
                };
              } else {
                element = {
                  id: results[i + 1].id,
                  order: results[i].order,
                };
              }
              payload.elements.push(element);
            }
            message = `El ${item.name} ha bajado ${newIndex - oldIndex} posiciones`;
          } else {
            for (let i = oldIndex; i >= newIndex; i--) {
              if (i == newIndex) {
                element = {
                  id: results[oldIndex].id,
                  order: results[newIndex].order,
                };
              } else {
                element = {
                  id: results[i - 1].id,
                  order: results[i].order,
                };
              }
              payload.elements.push(element);
            }
            message = `El ${item.name} ha subido ${oldIndex - newIndex} posiciones`;
          }
          break;
        }
        case "motive": {
          payload.pallete = "motives";
          payload.elements = [];
          const resultId = args[1];
          const results = this.typification.find((t) => t.id == groupId).results;
          const motives = results.find((t) => t.id == resultId).motives;
          let element;
          if (newIndex > oldIndex) {
            for (let i = oldIndex; i <= newIndex; i++) {
              if (i == newIndex) {
                element = {
                  id: motives[oldIndex].id,
                  order: motives[newIndex].order,
                };
              } else {
                element = {
                  id: motives[i + 1].id,
                  order: motives[i].order,
                };
              }
              payload.elements.push(element);
            }
            message = `El ${item.name} ha bajado ${newIndex - oldIndex} posiciones`;
          } else {
            for (let i = oldIndex; i >= newIndex; i--) {
              if (i == newIndex) {
                element = {
                  id: motives[oldIndex].id,
                  order: motives[newIndex].order,
                };
              } else {
                element = {
                  id: motives[i - 1].id,
                  order: motives[i].order,
                };
              }
              payload.elements.push(element);
            }
            message = `El ${item.name} ha subido ${oldIndex - newIndex} posiciones`;
          }
          break;
        }
        case "submotive": {
          payload.pallete = "submotives";
          payload.elements = [];
          const resultId = args[1];
          const motiveId = args[2];
          const results = this.typification.find((t) => t.id == groupId).results;
          const motives = results.find((t) => t.id == resultId).motives;
          const submotives = motives.find((t) => t.id == motiveId).submotives;
          let element;
          if (newIndex > oldIndex) {
            for (let i = oldIndex; i <= newIndex; i++) {
              if (i == newIndex) {
                element = {
                  id: submotives[oldIndex].id,
                  order: submotives[newIndex].order,
                };
              } else {
                element = {
                  id: submotives[i + 1].id,
                  order: submotives[i].order,
                };
              }
              payload.elements.push(element);
            }
            message = `El ${item.name} ha bajado ${newIndex - oldIndex} posiciones`;
          } else {
            for (let i = oldIndex; i >= newIndex; i--) {
              if (i == newIndex) {
                element = {
                  id: submotives[oldIndex].id,
                  order: submotives[newIndex].order,
                };
              } else {
                element = {
                  id: submotives[i - 1].id,
                  order: submotives[i].order,
                };
              }
              payload.elements.push(element);
            }
            message = `El ${item.name} ha subido ${oldIndex - newIndex} posiciones`;
          }
          break;
        }
        default:
          break;
      }
      await this.$store.dispatch("updateTypificationOrder", {
        portfolioId: this.$route.params.id,
        payload: payload,
      });
      this.$message.success(message);
    },
    async handleChange(item) {
      try {
        const response = await this.$store.dispatch("updateItemToTypificationTree", {
          ...item,
          portfolio: this.$route.params.id,
        });
        // if (response) {
        switch (item.type) {
          case "group":
            this.$message.success(`Se ha editado el grupo "${response.name}"`);
            break;
          case "result":
            this.$message.success(`Se ha editado el resultado "${response.name}"`);
            break;
          case "motive":
            this.$message.success(`Se ha editado el motivo "${response.name}"`);
            break;
          case "submotive":
            this.$message.success(`Se ha editado el submotivo "${response.name}"`);
            break;

          default:
            break;
        }
        this.$emit("updateTree");
        // } else {
        //   message.warning(`La tipificación "${item.name}" ya existe`);
        // }
        // this.$emit("updateTree");
      } catch (error) {
        const errorMessage = error?.getErrorMessage() || `Ha ocurrido un error`;

        this.$message.error(errorMessage);
      }
    },
    async handlePush(item) {
      try {
        await this.$store.dispatch("addItemToTypificationTree", {
          ...item,
          portfolio: this.$route.params.id,
        });
        // if (state.success) {
        switch (item.type) {
          case "group":
            this.$message.success(`Se ha creado un nuevo grupo "${item.name}"`);
            break;
          case "result":
            this.$message.success(`Se ha creado un nuevo resultado "${item.name}"`);
            break;
          case "motive":
            this.$message.success(`Se ha creado un nuevo motivo "${item.name}"`);
            break;
          case "submotive":
            this.$message.success(`Se ha creado un nuevo submotivo "${item.name}"`);
            break;
          default:
            break;
        }
        this.$emit("updateTree");
        // } else {
        //   if (state.status === 400)
        //     this.$message.warning(`La tipificación "${item.name}" ya existe`);
        //   else this.$message.error("Ha ocurrido un error");
        // }
      } catch (error) {
        const errorMessage = error?.getCleanErrorMessage() || `Ha ocurrido un error`;

        this.$message.error(errorMessage);
      }
    },
    hideDisabled(status) {
      if (status) {
        this.typification = this.filterArray(this.typification);
      } else {
        this.typification = this.copyTree;
      }
    },
    filterArray(arr) {
      return arr
        .filter((item) => item.status !== 2)
        .map((item) => {
          item = Object.assign({}, item);
          if (item.children) {
            item.children = this.filterArray(item.children);
          }
          return item;
        });
    },
    updateTree() {
      this.$emit("updateTree");
    },
  },
});
</script>

<style lang="sass" scoped>
.typification
  padding-right: 10px
  margin-top: 12px
</style>
